import { AfterViewInit, Component, EventEmitter, Inject, Input, Output } from '@angular/core';
import { VerifyPhoneService } from './verify-phone.service';
import { VerifyPhoneResult } from './typeing';
import * as firebase from 'firebase/app';
import { FIREBASE_REFERENCES } from '../../methods/firebase.module';
import {
  Auth,
  ConfirmationResult,
  PhoneAuthProvider,
  RecaptchaVerifier,
  signInWithCredential,
  signInWithPhoneNumber,
} from '@angular/fire/auth';
import { LiffService } from '../../liff.service';
import { CommonDialogService } from '../common-dialog/common-dialog.service';

@Component({
  selector: 'app-verify-phone',
  templateUrl: './verify-phone.component.html',
  styleUrls: ['./verify-phone.component.scss'],
  providers: [VerifyPhoneService],
})
export class VerifyPhoneComponent implements AfterViewInit {
  @Input() site = '';
  @Input() phone = '';
  @Input() optResendTime = 30;
  @Input() logoUrl = '';
  @Output() verifyPhoneResult: EventEmitter<VerifyPhoneResult> = new EventEmitter();

  public isProgress = true;
  public timer = 30;
  public errorMessage = '';
  public verificationCode = '';
  private verificationId = '';
  private recaptchaVerifier!: RecaptchaVerifier;
  public resendBtnDisabled = false;

  constructor(
    @Inject(FIREBASE_REFERENCES.US_FIREAUTH) private readonly usFireAuth: Auth,
    private liffService: LiffService,
    private commonDialogService: CommonDialogService
  ) {}

  async ngAfterViewInit() {
    this.recaptchaVerifier = new RecaptchaVerifier(this.usFireAuth, 'sign-in-button', { size: 'invisible' });

    setTimeout(() => {
      this.sendPhoneCode();
    }, 0);
  }

  checkForm() {
    return !!this.verificationCode;
  }

  otpTimer(time: number) {
    this.timer = time;
    const interval = setInterval(() => {
      this.timer--;
      if (this.timer < 0) {
        this.resendBtnDisabled = false;
        clearInterval(interval);
      }
    }, 1000);
  }

  checkVerificationCodeIsPhone() {
    return this.verificationCode.match(/^09\d{2}\s?\d{3}\s?\d{3}$/);
  }

  async sendPhoneCode() {
    this.isProgress = true;
    this.resendBtnDisabled = true;

    this.liffService.log(this.site || '', this.liffService.profile.userId, 'sendPhoneCode', this.phone);

    try {
      this.otpTimer(this.optResendTime);
      const inputPhone = `+886${this.phone.substring(1)}`;
      const confirmationResult = await signInWithPhoneNumber(this.usFireAuth, inputPhone, this.recaptchaVerifier);
      this.verificationId = confirmationResult.verificationId;
    } catch (error: any) {
      console.log(error);
      let errorCode = 10;
      if (error.code === 'auth/too-many-requests') {
        errorCode = 9;
      }

      this.liffService.log(
        this.site || '',
        this.liffService.profile.userId,
        'sendPhoneCodeError',
        this.phone,
        this.optResendTime,
        JSON.stringify({
          result: 'fail',
          phoneVerificationCode: this.verificationCode,
          resultMessage: this.errorMessage,
          inputPhone: `+886${this.phone.substring(1)}`,
          recaptchaVerifier: this.recaptchaVerifier,
          errorCode: errorCode,
          error: { message: error.message, error: error as Error },
        })
      );

      this.verifyPhoneResult.emit({
        result: 'fail',
        errorCode: errorCode,
        phoneVerificationCode: this.verificationCode,
        resultMessage: this.errorMessage,
      });
    } finally {
      this.isProgress = false;
    }
  }

  async _btnOtpVerify() {
    this.isProgress = true;
    // 等待個十秒
    this.errorMessage = '';
    try {
      // 後門通過規則
      if (this.verificationCode === this.phone) {
        this.liffService.log(
          this.site || '',
          this.liffService.profile.userId,
          'verifyPhoneSuccess',
          this.phone,
          JSON.stringify({
            result: 'success',
            phoneVerificationCode: this.verificationCode,
            resultMessage: '',
          })
        );

        this.verifyPhoneResult.emit({
          result: 'success',
          phoneVerificationCode: this.verificationCode,
          resultMessage: '',
        });
      } else {
        const credentials = PhoneAuthProvider.credential(this.verificationId, this.verificationCode);
        await signInWithCredential(this.usFireAuth, credentials);

        this.liffService.log(
          this.site || '',
          this.liffService.profile.userId,
          'verifyPhoneSuccess',
          this.phone,
          JSON.stringify({
            result: 'success',
            phoneVerificationCode: this.verificationCode,
            resultMessage: '',
          })
        );

        this.verifyPhoneResult.emit({
          result: 'success',
          phoneVerificationCode: this.verificationCode,
          resultMessage: '',
        });
      }
    } catch (error: any) {
      console.error(error);

      let errorCode = 10;

      if (error.code === 'auth/invalid-verification-code') {
        errorCode = 7;
      } else if (error.code === 'auth/code-expired') {
        errorCode = 8;
      }

      this.liffService.log(
        this.site || '',
        this.liffService.profile.userId,
        'verifyPhoneError',
        this.phone,
        JSON.stringify({
          result: 'fail',
          phoneVerificationCode: this.verificationCode,
          resultMessage: this.errorMessage,
          errorCode: errorCode,
          error: { message: error.message, error: error as Error },
        })
      );

      this.verifyPhoneResult.emit({
        result: 'fail',
        errorCode: errorCode,
        phoneVerificationCode: this.verificationCode,
        resultMessage: this.errorMessage,
      });
    } finally {
      this.isProgress = false;
    }
  }

  _btnPrev() {
    this.verifyPhoneResult.emit({
      result: 'cancel',
      phoneVerificationCode: this.verificationCode,
      resultMessage: '',
    });
  }

  async _btnSkip() {
    try {
      const isConfirmSkip = await this.commonDialogService.openCommonDialog(13, { isShowCancelButton: true });
      if (isConfirmSkip) {
        this.liffService.log(
          this.site || '',
          this.liffService.profile.userId,
          'skipVerifyPhone',
          this.phone,
          JSON.stringify({
            result: 'skip',
            phoneVerificationCode: this.verificationCode,
            resultMessage: 'skip',
          })
        );

        this.verifyPhoneResult.emit({
          result: 'skip',
          phoneVerificationCode: this.verificationCode,
          resultMessage: 'skip',
        });
      }
    } catch (error: any) {
      console.error(error);

      this.liffService.log(
        this.site || '',
        this.liffService.profile.userId,
        'skipVerifyPhoneError',
        this.phone,
        JSON.stringify({
          phoneVerificationCode: this.verificationCode,
          resultMessage: this.errorMessage,
          error: { message: error.message, error: error as Error },
        })
      );

      const errorCode = 9;
      this.verifyPhoneResult.emit({
        result: 'fail',
        errorCode: errorCode,
        phoneVerificationCode: this.verificationCode,
        resultMessage: this.errorMessage,
      });
    }

    return;
  }

  // onInputChange(event: Event): void {

  //   setTimeout(() => {
  //     const inputEvent = event as InputEvent;
  //     const inputElement = event.target as HTMLInputElement;
  //     // 根據 inputType 區分手動輸入還是自動填充
  //     if (inputEvent.inputType === 'insertReplacementText' || inputEvent.inputType === 'insertCompositionText' || inputEvent.inputType === 'insertFromPaste') {
  //       // 從 inputEvent.data 取得輸入的值
  //       const otpValue = inputElement.value;
  //       inputElement.value = otpValue.slice(0, 6);
  //     }
  //   }, 50); // 50ms 延遲，確保自動填充完成

  // }
}
